import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router';
import { useReportsState } from '@reports/presentation/contexts';
import { BlockDataRequestDto } from '@reports/application/dtos';
import useUserStore from 'hooks/useUserStore';
import { shouldClearFiltersByMetadata, recalculateRelativePresetDates } from '@reports/presentation/utils';
import { getFiltersUseCases } from '@reports/composition';

export type UseReportFiltersManagementProps = {
  autoLoad?: boolean;
  autoSave?: boolean;
};

export const useReportFiltersManagement = ({ autoLoad = true, autoSave = true }: UseReportFiltersManagementProps) => {
  const { state, actions } = useReportsState();
  const location = useLocation();
  const { storeUuid } = useUserStore();

  // Get use cases from composition root
  const {
    saveAppliedFilters,
    getAppliedFilters,
    clearAppliedFilters,
    getFiltersMetadata,
    saveFiltersMetadata,
  } = getFiltersUseCases();

  // Load into state the filters saved in storage
  useEffect(() => {
    if (autoLoad) {
      const loadSavedFilters = async () => {
        try {
          // Get stored metadata to check if context changed
          const storedMetadata = await getFiltersMetadata.execute();
          const currentReportPath = location.pathname;

          // Check if filters should be cleared due to store or path change
          const shouldClear = shouldClearFiltersByMetadata(storedMetadata, storeUuid, currentReportPath);

          if (shouldClear) {
            // Clear filters and metadata if context changed
            await clearAppliedFilters.execute();
            actions.resetFilters();
          } else {
            // Load filters if context hasn't changed
            const savedFilters = await getAppliedFilters.execute();

            if (savedFilters) {
              // Recalculate dates for relative presets (TODAY, YESTERDAY, THIS_WEEK, etc.)
              const filtersWithRecalculatedDates = recalculateRelativePresetDates(savedFilters);
              actions.updateFilters(filtersWithRecalculatedDates);
            }

            // Update metadata with current context after successful load
            if (storeUuid && currentReportPath) {
              await saveFiltersMetadata.execute({
                storeUuid,
                reportPath: currentReportPath,
              });
            }
          }
        } catch {
          // Do nothing, load the default filters
        } finally {
          // Mark as initialized after loading (success or failure)
          actions.setInitialized();
        }
      };

      loadSavedFilters();
    } else {
      // If not auto-loading, mark as initialized immediately
      actions.setInitialized();
    }
  }, [autoLoad, storeUuid, location.pathname]);

  // Save the filters to storage when the filters change
  useEffect(() => {
    if (autoSave && state.isInitialized) {
      const saveFilters = async () => {
        try {
          await saveAppliedFilters.execute(state.appliedFilters);

          // Update metadata with current context after saving filters
          if (storeUuid && location.pathname) {
            await saveFiltersMetadata.execute({
              storeUuid,
              reportPath: location.pathname,
            });
          }
        } catch {
          // Do nothing, works without saving the filters
        }
      };

      saveFilters();
    }
  }, [autoSave, state.appliedFilters, storeUuid, location.pathname]);

  // Manual load function to load the filters to state from storage
  const loadFilters = useCallback(async () => {
    try {
      const savedFilters = await getAppliedFilters.execute();
      if (savedFilters) {
        // Recalculate dates for relative presets (TODAY, YESTERDAY, THIS_WEEK, etc.)
        const filtersWithRecalculatedDates = recalculateRelativePresetDates(savedFilters);
        actions.updateFilters(filtersWithRecalculatedDates);
      }
    } catch {
      // Do nothing, load the default filters
    }
  }, [getAppliedFilters, actions]);

  // Manual save function to save the filters from state to storage
  const saveFilters = useCallback(
    async (filters?: BlockDataRequestDto) => {
      const filtersToSave = filters ?? state.appliedFilters;

      try {
        await saveAppliedFilters.execute(filtersToSave);
      } catch {
        // Do nothing, works without saving the filters
      }
    },
    [state.appliedFilters, saveAppliedFilters],
  );

  // Clear saved filters from storage and state
  const clearFilters = useCallback(async () => {
    try {
      await clearAppliedFilters.execute();
      actions.resetFilters();
    } catch {
      // Do nothing, works without clearing the filters
    }
  }, [actions, clearAppliedFilters]);

  return {
    appliedFilters: state.appliedFilters,
    updateFilters: actions.updateFilters,
    resetFilters: actions.resetFilters,
    selectReport: actions.selectReport,
    loadFilters,
    saveFilters,
    clearFilters,
  };
};
